import type { BundledLanguage } from 'shiki';

export type PreviewKind =
  | 'code'
  | 'markdown'
  | 'html'
  | 'pdf'
  | 'image'
  | 'video'
  | 'audio'
  | 'csv'
  | 'unknown';

function extOf(path: string): string {
  const base = path.split('/').pop() ?? path;
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return base.toLowerCase(); // dotfiles / no ext
  return base.slice(dot + 1).toLowerCase();
}

const CODE_EXTS = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'py', 'rb', 'go', 'rs', 'java', 'kt',
  'c', 'h', 'cpp', 'cc', 'hpp', 'cs', 'php', 'swift', 'sh', 'bash', 'zsh', 'fish',
  'json', 'jsonc', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'env', 'xml',
  'css', 'scss', 'sass', 'less', 'sql', 'graphql', 'gql', 'proto', 'dockerfile',
  'makefile', 'lua', 'r', 'dart', 'vue', 'svelte', 'astro', 'txt', 'text', 'log',
  'gitignore', 'gitattributes', 'editorconfig', 'lock', 'diff', 'patch', 'tsv',
]);

/**
 * Route a file to a previewer by mime first, falling back to extension. This is
 * intentionally forgiving: canonical mimes are trusted, but text files often
 * arrive as `application/octet-stream` or `text/plain`, so extension wins there.
 */
export function categorizePreview(mime: string | undefined, path: string): PreviewKind {
  const m = (mime ?? '').toLowerCase().split(';')[0].trim();
  const ext = extOf(path);

  // Extension-driven for the ambiguous cases first (markdown/html/csv are all text/*).
  if (ext === 'md' || ext === 'markdown' || ext === 'mdx' || m === 'text/markdown') return 'markdown';
  if (ext === 'html' || ext === 'htm' || m === 'text/html') return 'html';
  if (ext === 'csv' || ext === 'tsv' || m === 'text/csv' || m === 'text/tab-separated-values') return 'csv';
  if (ext === 'pdf' || m === 'application/pdf') return 'pdf';

  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';

  if (m.startsWith('text/') || m === 'application/json' || m === 'application/xml' || m.includes('javascript')) {
    return 'code';
  }
  if (CODE_EXTS.has(ext)) return 'code';

  // image/video/audio by extension when mime is generic (octet-stream).
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'bmp', 'ico'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'm4v', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) return 'audio';

  return 'unknown';
}

const LANG_MAP: Record<string, BundledLanguage> = {
  ts: 'typescript', tsx: 'tsx', js: 'javascript', jsx: 'jsx', mjs: 'javascript', cjs: 'javascript',
  py: 'python', rb: 'ruby', go: 'go', rs: 'rust', java: 'java', kt: 'kotlin',
  c: 'c', h: 'c', cpp: 'cpp', cc: 'cpp', hpp: 'cpp', cs: 'csharp', php: 'php', swift: 'swift',
  sh: 'bash', bash: 'bash', zsh: 'bash', fish: 'fish',
  json: 'json', jsonc: 'jsonc', yaml: 'yaml', yml: 'yaml', toml: 'toml', ini: 'ini', cfg: 'ini', conf: 'ini',
  xml: 'xml', css: 'css', scss: 'scss', sass: 'sass', less: 'less', sql: 'sql',
  graphql: 'graphql', gql: 'graphql', proto: 'proto', lua: 'lua', r: 'r', dart: 'dart',
  vue: 'vue', svelte: 'svelte', astro: 'astro', dockerfile: 'docker', makefile: 'make',
  md: 'markdown', markdown: 'markdown', mdx: 'mdx', diff: 'diff', patch: 'diff', html: 'html', htm: 'html',
};

/** Best-effort Shiki language id. Falls back to plaintext (`text`, always loaded). */
export function shikiLanguage(path: string): BundledLanguage {
  const ext = extOf(path);
  return LANG_MAP[ext] ?? ('text' as BundledLanguage);
}

export interface ParsedCsv {
  rows: string[][];
  truncated: boolean;
  totalRows: number;
}

/**
 * Minimal RFC-4180-ish CSV/TSV parser: quoted fields, escaped quotes, and
 * embedded newlines. Returns at most `maxRows` rows.
 */
export function parseCsv(text: string, delimiter = ',', maxRows = 500): ParsedCsv {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let total = 0;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    total += 1;
    if (rows.length < maxRows) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      pushField();
    } else if (ch === '\n') {
      pushRow();
    } else if (ch === '\r') {
      // swallow — handled by the following \n
    } else {
      field += ch;
    }
  }
  // Trailing field/row if the file doesn't end in a newline.
  if (field.length > 0 || row.length > 0) pushRow();

  return { rows, truncated: total > rows.length, totalRows: total };
}

export function guessCsvDelimiter(path: string, mime?: string): string {
  const ext = extOf(path);
  if (ext === 'tsv' || (mime ?? '').includes('tab-separated')) return '\t';
  return ',';
}
